import { ErrorState, Skeleton } from "@common/index";
import { ROUTES } from "@routing/routes";
import type { HomePromotion } from "@models/promotion";
import { useBrowseHome, POPULAR_LIMIT } from "./hooks/useBrowseHome";
import { toPromoBannerProps } from "./_helpers/toPromoBannerProps";
import { HeroBanner } from "./components/HeroBanner";
import { PromoBanner } from "./components/PromoBanner";
import { CategoryChipRow } from "./components/CategoryChipRow";
import { ProductCarouselRow } from "./components/ProductCarouselRow";
import { ProductGrid } from "./components/ProductGrid";

// The tiles row takes as many columns as it has live tiles, so one or two
// tiles fill the row rather than leaving holes. Literal class names, so
// Tailwind's scanner sees each one.
const TILE_GRID_COLUMNS: Record<number, string> = {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
};
const TILE_SKELETON_COUNT = 3;

export const BrowseHome = () => {
    const {
        popularCategories,
        popularProducts,
        featuredProducts,
        isLoadingFeatured,
        hasFeaturedError,
        retryFeatured,
        promotionsRead,
        productPage,
        isLoadingProducts,
        hasProductsError,
        retryProducts,
        isLoadingPopular,
        hasPopularError,
        retryPopular,
        isLoadingCategories,
        hasCategoriesError,
        retryCategories,
        pageNumber,
        setPage,
    } = useBrowseHome();

    // Optional promo banners: hidden on a failed read by design (E14-S5),
    // not a failed state — like the optional recommendation rails (#223
    // ruling). A failed read and an empty slot both render nothing, and the
    // rest of the page never waits on this read.
    const isLoadingPromotions = promotionsRead.status === "loading";
    const promotions =
        promotionsRead.status === "loaded"
            ? promotionsRead.promotions
            : undefined;
    // Null tiles are dropped; the live ones keep their Tile1→Tile3 order.
    const tiles = (promotions?.tilesRow ?? []).filter(
        (tile): tile is HomePromotion => tile !== null,
    );

    return (
        <div data-testid="browse-home" className="bg-base-100">
            {isLoadingPromotions && (
                <Skeleton
                    data-testid="hero-banner-skeleton"
                    className="w-full h-48 md:h-64 rounded-none"
                />
            )}
            {promotions?.topHero && (
                <HeroBanner {...toPromoBannerProps(promotions.topHero)} />
            )}

            <div className="max-w-7xl mx-auto px-2 md:px-4">
                {/* Every "View all" reaches its own page (#401) — none of them
                    may point back at this one. */}
                <CategoryChipRow
                    title="Popular Categories"
                    categories={popularCategories}
                    viewAllHref={ROUTES.BROWSE_CATEGORIES}
                    isLoading={isLoadingCategories}
                    hasError={hasCategoriesError}
                    onRetry={retryCategories}
                    errorMessage="Couldn't load popular categories."
                    skeletonCount={POPULAR_LIMIT}
                />

                {/* Hidden once loaded with nothing (B-02): until a category
                    pins a product the rail has nothing to say, and a heading
                    over an empty space reads as broken. In flight it keeps
                    its placeholders, and a failed read keeps its own failed
                    state, so only a real "none" hides it. */}
                {(isLoadingFeatured ||
                    hasFeaturedError ||
                    featuredProducts.length > 0) && (
                    <ProductCarouselRow
                        title="Featured Products"
                        products={featuredProducts}
                        viewAllHref={ROUTES.BROWSE_FEATURED}
                        isLoading={isLoadingFeatured}
                        hasError={hasFeaturedError}
                        onRetry={retryFeatured}
                        errorMessage="Couldn't load featured items."
                        skeletonCount={POPULAR_LIMIT}
                        priority
                    />
                )}

                {isLoadingPromotions && (
                    <>
                        <div className="md:hidden px-2 py-4">
                            <Skeleton
                                data-testid="promo-banner-skeleton"
                                className="w-full h-32 rounded-lg"
                            />
                        </div>
                        <div className="hidden md:grid md:grid-cols-3 gap-4 py-4">
                            {Array.from({ length: TILE_SKELETON_COUNT }).map(
                                (_, i) => (
                                    <Skeleton
                                        key={i}
                                        data-testid="promo-tile-skeleton"
                                        className="w-full h-32 rounded-lg"
                                    />
                                ),
                            )}
                        </div>
                    </>
                )}

                {promotions?.midPagePromo && (
                    <div
                        data-testid="promo-mid-page"
                        className="md:hidden px-2 py-4"
                    >
                        <PromoBanner
                            {...toPromoBannerProps(promotions.midPagePromo)}
                        />
                    </div>
                )}

                {tiles.length > 0 && (
                    <div
                        data-testid="promo-tiles-row"
                        className={`hidden md:grid ${TILE_GRID_COLUMNS[tiles.length]} gap-4 py-4`}
                    >
                        {tiles.map((tile) => (
                            <PromoBanner
                                key={tile.id}
                                {...toPromoBannerProps(tile)}
                            />
                        ))}
                    </div>
                )}

                <ProductCarouselRow
                    title="Most Popular"
                    products={popularProducts}
                    viewAllHref={ROUTES.BROWSE_POPULAR}
                    isLoading={isLoadingPopular}
                    hasError={hasPopularError}
                    onRetry={retryPopular}
                    errorMessage="Couldn't load popular products."
                    skeletonCount={POPULAR_LIMIT}
                />

                {hasProductsError ? (
                    <ErrorState onRetry={retryProducts} />
                ) : (
                    <ProductGrid
                        products={productPage.items}
                        pageNumber={pageNumber}
                        totalPages={productPage.totalPages}
                        isLoading={isLoadingProducts}
                        onPageChange={setPage}
                    />
                )}
            </div>
        </div>
    );
};
